import { capitalizeText } from '@repo/utils/strings';
import cn from 'classnames';

import { Tag } from '../../Tag/Tag';
import inputStyles from '../Input.module.css';
import { RootWrapper } from '../RootWrapper';

import styles from './TagInput.module.css';
import { TagInputProps, useTagInputProps } from './useTagInputProps';

/**
 * Internal component used by the TagInput to handle input and tag display
 */
const TagInputInternal: React.FC<TagInputProps> = (props: TagInputProps) => {
  const {
    value = [],
    capitalize = false,
    state,
    placeholder = 'Add tags',
  } = props;
  const {
    inputRef,
    inputValue,
    showTextInput,
    showPlaceholder,
    inputProps,
    handleKeyDown,
    handleChange,
    handleBlur,
    removeTag,
  } = useTagInputProps(props);

  const enhancedInputProps = {
    ...inputProps,
    ref: inputRef,
    'aria-invalid': state === 'critical',
    className: `${styles.input} ${inputStyles['input-component']}`,
    disabled: props.disabled,
    value: inputValue,
    onBlur: handleBlur,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    placeholder: showPlaceholder ? placeholder : '',
  };

  return (
    <div className={styles['tags-area']}>
      {value.map((tag: string) => (
        <Tag
          key={tag}
          preventTextOverflow
          isCritical={state === 'critical'}
          name="tag"
          size="xs"
          onClose={
            props.disabled
              ? undefined
              : () => {
                  removeTag(tag);
                }
          }
        >
          {capitalize ? capitalizeText(tag) : tag}
        </Tag>
      ))}

      {showTextInput && (
        <div aria-disabled={props.disabled} className={styles['input-wrapper']}>
          <input {...enhancedInputProps} />
        </div>
      )}
    </div>
  );
};

/**
 * TagInput component for entering and managing multiple tags
 */
export const TagInput = (props: TagInputProps) => {
  return (
    <RootWrapper
      {...props}
      aria-label="Tag input field"
      className={cn(
        styles['tag-input-container'],
        { [styles['tag-input-large']]: props.isLarge },
        props.className
      )}
    >
      <TagInputInternal {...props} />
    </RootWrapper>
  );
};

export type { TagInputProps };
